'use client';

import { useQuery } from '@tanstack/react-query';
import { getInteractionAggregates } from '@/api/generated/interactions/interactions';
import type { TimeRangeConfig } from '@/api/generated/model';
import type { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { QUERY_KEY_INTERACTION_AGGREGATES } from '@/constants/query-keys';
import { buildAiSpendTaskModelPayload } from '@/domains/pages/ai-spend/utils/ai-spend-api-payloads';
import { mapTaskModelChartData, type TaskModelSeries } from '@/domains/pages/ai-spend/utils/map-ai-spend-task-model';
import { NEBULY_PAGE_QUERY_OPTIONS, getPageTimeRangeQueryKey } from '@/domains/pages/shared/page-query-options';
import { resolveNebulyPageTimeRange } from '@/domains/pages/shared/page-time-range';
import { useNebulyPageEffectiveNow } from '@/domains/pages/hooks/use-nebuly-page-effective-now';
import { useNebulyPageStableNow } from '@/domains/pages/hooks/use-nebuly-page-stable-now';
import { getTimeRangeMemoKey } from '@/domains/pages/utils/nebuly-page-time-range';
import { useCallback, useMemo } from 'react';
export type AiSpendTaskModelData = {
  taskLabels: string[];
  series: TaskModelSeries[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};
type UseAiSpendTaskModelDataParams = {
  projectId: string | undefined;
  timeRange: TimeRangeConfig | undefined;
  filters?: GenericFilter[];
  specialFilters?: GenericFilter[];
  enabled?: boolean;
};
export function useAiSpendTaskModelData({
  projectId,
  timeRange,
  filters = [],
  specialFilters = [],
  enabled = true
}: UseAiSpendTaskModelDataParams): AiSpendTaskModelData {
  const stableNow = useNebulyPageStableNow();
  const {
    effectiveNow,
    isReady: isEffectiveNowReady
  } = useNebulyPageEffectiveNow();
  const stableNowKey = stableNow.getTime();
  const effectiveNowKey = effectiveNow.getTime();
  const timeRangeMemoKey = getTimeRangeMemoKey(timeRange);
  const resolvedTimeRange = useMemo(() => resolveNebulyPageTimeRange(timeRange, stableNow, effectiveNow), [timeRangeMemoKey, stableNowKey, effectiveNowKey]);
  const timeRangeQueryKey = useMemo(() => getPageTimeRangeQueryKey(resolvedTimeRange), [resolvedTimeRange]);
  const filtersQueryKey = useMemo(() => JSON.stringify(filters), [filters]);
  const specialFiltersQueryKey = useMemo(() => JSON.stringify(specialFilters), [specialFilters]);
  const queryEnabled = enabled && Boolean(projectId && resolvedTimeRange && isEffectiveNowReady);
  const taskModelQuery = useQuery({
    queryKey: [QUERY_KEY_INTERACTION_AGGREGATES, 'ai-spend-task-model', projectId, timeRangeQueryKey, filtersQueryKey, specialFiltersQueryKey],
    queryFn: ({
      signal
    }) => getInteractionAggregates(buildAiSpendTaskModelPayload({
      projectId: projectId!,
      timeRange: resolvedTimeRange!,
      filters,
      specialFilters
    }), {
      signal
    }),
    enabled: queryEnabled,
    ...NEBULY_PAGE_QUERY_OPTIONS
  });
  const chartData = useMemo(() => {
    if (taskModelQuery.data) {
      return mapTaskModelChartData(taskModelQuery.data);
    }
    return {
      taskLabels: [],
      series: []
    };
  }, [taskModelQuery.data]);
  const refetch = useCallback(() => {
    void taskModelQuery.refetch();
  }, [taskModelQuery.refetch]);
  return {
    taskLabels: chartData.taskLabels,
    series: chartData.series,
    isLoading: queryEnabled && taskModelQuery.isLoading,
    isError: taskModelQuery.isError,
    error: taskModelQuery.error,
    refetch
  };
}